import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from '../common/Config';
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const DsrReport = () => {
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState('');
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [salesRap, setSalesRap] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [dsrReport, setDsrReport] = useState([]);
  const [totals, setTotals] = useState({ order_value: 0, amount_collected: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleDateChange = (value) => {
    setDates(value);
  };

  const searchFormData = () => {
    if (dates[0] && dates[1]) {
      const startDate = format(dates[0], 'dd/MM/yyyy');
      const endDate = format(dates[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setPage(0);
    }
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const body = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: body.entries().next().done ? undefined : body
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = (data.data || []).map(item => ({
        key: `${item.first_name} ${item.last_name}`,
        value: item.id,
        label: `${item.first_name} ${item.last_name}`,
      }));
      setSalesRapOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchQuery,
        reportrange,
        sales_rep_id: salesRap || ''
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/dsr_report_data`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setDsrReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        if (json.totals) {
          setTotals({
            order_value: parseFloat(json.totals.order_value || 0),
            amount_collected: parseFloat(json.totals.amount_collected || 0)
          });
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, salesRap, searchQuery]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
  }, []);

  useEffect(() => {
    if (reportrange) {
      fetchReport();
    }
  }, [reportrange, page, rowsPerPage, salesRap, searchQuery, fetchReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRap(newValue ? newValue.value : '');
    setPage(0);
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: '1',
      start: '0',
      length: totalCount || 10000,
      searchValue: searchQuery,
      reportrange,
      sales_rep_id: salesRap || ''
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    try {
      const response = await fetch(`${config.API_URL}/dsr_report_data`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch data');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const tableData = json.data || [];

      const formattedData = tableData.map(row => ({
        Date: row.visit_date || '',
        Time: row.time_of_visit || '',
        "Customer Name(A/C)": `${row.company || ''} (${row.accound_no || ''})`,
        "Ordered / Visit": row.orderOrVisit || '',
        "Order Value": row.order_value || '',
        "Amt Collected": row.amount_collected || '',
        "Pay Mode": row.payment_mode || '',
        Reason: row.reason_or_description || '',
        "Sales Rep": row.salesRep_name || '',
        Address: row.address || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "DSR Report");
      XLSX.writeFile(workbook, "DSR_Report.xlsx");
    } catch (error) {
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
              <Typography className="page-title">
                Daily Sales Report
              </Typography>
            </Box>

            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} sx={{ color: "#0e0f0fef" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                              setPage(0);
                            }}
                            sx={{ color: "#637381" }}
                          >
                            <Iconify icon="eva:close-fill" width={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
             
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={salesRapOptions}
                  value={selectedSalesRep}
                  onChange={handleSalesRepChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border text-field"
                      {...params}
                      placeholder="Select Sales Rep"
                      size="small"
          
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }} display="flex" alignItems="flex-end" flexDirection={{ xs: "column", sm: "row" }} gap={1}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={searchFormData}
                  fullWidth
                  sx={{
                    width: { xs: "100%", sm: "auto", md: "auto" },
                  }}
                >
                  Submit
                </Button>
                <Button
                  
                  variant="outlined"
                  startIcon={<Iconify icon="eva:download-outline" width={20} />}
                  onClick={exportToExcel}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    whiteSpace: "nowrap", 
                    minWidth: "140px", 
                  }}
                  className="button-variant-outlined"
                >
                  Export to Excel
                </Button>
              </Grid2>
            </Grid2>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer>
                <Table size="medium" className="table-cell-border">
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                        sx={{
                          width: "110px",
                     
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "80px",
                        }}
                      >
                        Time
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "250px",
                        }}
                      >
                        Customer Name(A/C)
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "110px",
                        }}
                      >
                        Order / Visit
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          textAlign: "right",
                        }}
                      >
                        Order Value
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                          textAlign: "right",
                        }}
                      >
                        Amt Collected
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Pay Mode
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                        }}
                      >
                        Sales Rep
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "150px",
                          minWidth: "150px",
                        }}
                      >
                        Reason
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "200px",
                          minWidth: "200px",
                        }}
                      >
                        Address
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dsrReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: "#0e0f0fef" }}>
                            {reportrange ? "No data available" : "Please select a date range and click Submit"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dsrReport.map((row, index) => (
                        <TableRow
                          key={index}
                          className="table-cell-style"
                          sx={{
                
                            "&:last-child td": {
                              borderBottom: "none",
                            },
                          
                          }}
                        >
                          <TableCell
                 
                          >
                            {row.visit_date || ""}
                          </TableCell>
                          <TableCell
                       
                          >
                            {row.time_of_visit || ""}
                          </TableCell>
                          <TableCell
                        
                          >
                            {row.company ? `${row.company} ( ${row.accound_no || ''} )` : ""}
                          </TableCell>
                          <TableCell
                          >
                            {row.orderOrVisit || ""}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {formatNumber(row.order_value)}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {formatNumber(row.amount_collected)}
                          </TableCell>
                          <TableCell
                      
                          >
                            {row.payment_mode || ""}
                          </TableCell>
                          <TableCell
                    
                          >
                            {row.salesRep_name || ""}
                          </TableCell>
                          <TableCell
                     
                          >
                            {row.reason_or_description || ""}
                          </TableCell>
                          <TableCell
                      
                          >
                            {row.address || ""}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow sx={{ "& .MuiTableCell-root": { fontSize: "0.75rem", fontWeight: 600, color: "#212b36", py: 1.2, textAlign: "right", backgroundColor: "#f5f7fa", borderBottom: "none" } }}>
                      <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                      <TableCell
                        sx={{
                          textAlign: "right",
                        }}
                      >
                        {formatNumber(totals.order_value)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                        }}
                      >
                        {formatNumber(totals.amount_collected)}
                      </TableCell>
                      <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}

            {!loading && dsrReport.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                  className="pagination-styles"
                />
              </Box>
            )}
      
        </Paper>
      </Box>

    </>
  );
};

export default DsrReport;
